import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, ChevronRight } from "lucide-react";
import api, { formatMoney } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";

const statusMap = {
  pending: "bg-slate-100 text-slate-700",
  awaiting_payment: "bg-amber-100 text-amber-800",
  paid: "bg-emerald-100 text-emerald-800",
  processing: "bg-blue-100 text-blue-800",
  shipped: "bg-purple-100 text-purple-800",
  delivered: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-red-100 text-red-800",
};

const MyOrders = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/orders").then((res) => setOrders(res.data)).catch(() => setOrders([]));
  }, [user]);

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-black text-slate-900">Please sign in to view your orders</h1>
        <Link to="/login" className="mt-4 inline-block text-[#0b3d75] font-semibold hover:text-amber-600">Sign In</Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-black text-slate-900">My Orders</h1>
      <p className="text-slate-500 mt-1">Hi {user.first_name}, here are your recent orders.</p>

      <div className="mt-6 space-y-3">
        {orders === null ? (
          Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-28 w-full" />)
        ) : orders.length === 0 ? (
          <div className="text-center py-16 bg-slate-50 rounded-xl">
            <Package className="w-10 h-10 mx-auto text-slate-400" />
            <div className="mt-3 font-bold text-slate-900">No orders yet</div>
            <Link to="/shop" className="mt-2 inline-block text-[#0b3d75] font-semibold hover:text-amber-600">Start shopping</Link>
          </div>
        ) : (
          orders.map((o) => (
            <Link
              key={o.id}
              to={`/order/${o.order_number}`}
              className="block bg-white border border-slate-200 rounded-lg p-5 hover:border-[#0b3d75]/40 hover:shadow-md transition-all"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-xs text-slate-500">Order</div>
                  <div className="font-black text-slate-900">{o.order_number}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Placed</div>
                  <div className="font-medium text-slate-800">{new Date(o.created_at).toLocaleDateString()}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Total</div>
                  <div className="font-black text-[#0b3d75]">{formatMoney(o.total)}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Status</div>
                  <Badge className={statusMap[o.status]}>{o.status.replace(/_/g, " ")}</Badge>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </div>
              <div className="mt-3 flex gap-2 overflow-x-auto">
                {o.items.slice(0, 6).map((it) => (
                  <div key={it.product_id} className="w-12 h-12 rounded border bg-slate-50 shrink-0 overflow-hidden">
                    {it.image && <img src={it.image} alt="" className="w-full h-full object-contain" />}
                  </div>
                ))}
                <div className="text-xs text-slate-500 self-center">{o.items.length} item{o.items.length === 1 ? "" : "s"}</div>
              </div>
            </Link>
          ))
        )}
      </div>
    </div>
  );
};

export default MyOrders;
